import React from "react";
import classNames from "classnames/bind";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import styles from "./ThemeToggle.module.scss";

const cx = classNames.bind(styles);

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

interface ThemeToggleProps {
  className?: string;
}

/**
 * Two-state toggle: light ↔ dark. Initial value picks up the user's OS
 * preference; clicking flips to the opposite and persists. Hidden on auth
 * / landing routes where the theme is always light.
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({ className }) => {
  const { t } = useTranslation();
  const { theme, toggleTheme, isLightLocked } = useTheme();

  if (isLightLocked) return null;

  // Show the icon representing what the user will switch INTO on click:
  // in light mode → moon (click to go dark), in dark mode → sun (click to
  // go light). Matches the common OS pattern.
  const nextTheme = theme === "dark" ? "light" : "dark";
  const title = t(`theme.${nextTheme}`);
  const Icon = theme === "dark" ? SunIcon : MoonIcon;

  return (
    <button
      type="button"
      className={cx("themeToggle", className)}
      onClick={toggleTheme}
      aria-label={title}
      title={title}
    >
      <Icon />
    </button>
  );
};

export default ThemeToggle;
